/**
 * Checking in an unpaid booking (DNKADM-0402 U5) — the one prompt the desk sees when a
 * pay-later booking arrives: take the money now, or check them in and collect later.
 *
 * **Check-in is never blocked by Unpaid** (Operator guardrail, 2026-09-22). The member
 * may pay after playing, so "Check in without payment" is a first-class action here,
 * not a hidden escape: it checks them in and leaves the booking Unpaid, where the badge
 * and the drawer's Take payment keep it visible. Nothing is charged automatically —
 * not here, not at check-in, not later.
 *
 * DNKAPI-0690 / DNKADM-0475: on a No-show booking whose fee is still queued, checking in
 * without payment (`charge:false`) cancels that fee, which is a waive under the DNKADM-0469
 * rules. STAFF (`canWaiveNoShowFee` `'pin'`) enters the manager PIN first; at a location with
 * no PIN set (`false`) the action is held, with the reason. Every other booking is unchanged.
 */
import { useId, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, resolveTokens } from '@/theme';
import type { Reservation } from '@/api/reservations';
import { useMoneyPermissions } from '@/auth/moneyPermissions';
import { ManagerPinField } from './ManagerPinField';
import { isNoShowFeeQueued, NO_SHOW_FEE_WAIVE_NEEDS_MANAGER } from './noShowFee';
import {
  amountDueLabel,
  isPaymentInProgress,
  PAYMENT_IN_PROGRESS_HELP,
} from './calendar/derive';

interface UnpaidCheckInDialogProps {
  open: boolean;
  reservation: Reservation;
  submitting?: boolean;
  onClose: () => void;
  /** Open Take payment for this booking. Not offered for an in-progress payment. */
  onTakePayment: () => void;
  /**
   * Check them in and leave the booking Unpaid. `managerPin` is set only when STAFF waives
   * a queued no-show fee by doing so (DNKADM-0475).
   */
  onCheckInAnyway: (managerPin?: string) => void;
}

export function UnpaidCheckInDialog({
  open,
  reservation,
  submitting = false,
  onClose,
  onTakePayment,
  onCheckInAnyway,
}: UnpaidCheckInDialogProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const titleId = `${useId()}-title`;
  const amount = amountDueLabel(reservation);
  // A payment pay-api never confirmed is NOT payable from here (Operator decision,
  // 2026-09-22) — the prompt drops the offer rather than disabling it.
  const inProgress = isPaymentInProgress(reservation);
  const label = inProgress ? 'Payment in progress' : 'Unpaid';
  const { canWaiveNoShowFee } = useMoneyPermissions(reservation.locationId);
  const feeQueued = isNoShowFeeQueued(reservation);
  const pinNeeded = feeQueued && canWaiveNoShowFee === 'pin';
  const waiveBlocked = feeQueued && canWaiveNoShowFee === false;
  const [managerPin, setManagerPin] = useState('');
  const pin = managerPin.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ fontSize: 18, fontWeight: 500, pb: 0.5 }}>
        {inProgress
          ? amount === null
            ? `${label} — check them in?`
            : `${label} · ${amount} — check them in?`
          : amount === null
            ? `${label} — take payment now?`
            : `${label} · ${amount} — take payment now?`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: t.text2 }}>
          {inProgress
            ? PAYMENT_IN_PROGRESS_HELP
            : 'This booking hasn’t been paid for. You can take payment now, or check them in and collect later — they won’t be charged automatically.'}
        </Typography>
        {pinNeeded && (
          <>
            <Typography variant="body2" sx={{ color: t.text2, mt: 1.5 }}>
              Checking in without payment cancels the no-show fee. Enter the manager PIN to do it.
            </Typography>
            <ManagerPinField
              value={managerPin}
              onChange={setManagerPin}
              disabled={submitting}
              error={null}
              sx={{ mt: 1.5 }}
            />
          </>
        )}
        {waiveBlocked && (
          <Typography variant="body2" sx={{ color: t.text2, mt: 1.5 }}>
            {NO_SHOW_FEE_WAIVE_NEEDS_MANAGER}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5, gap: 1 }}>
        <Button color="inherit" onClick={onClose} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => onCheckInAnyway(pinNeeded ? pin : undefined)}
          disabled={submitting || waiveBlocked || (pinNeeded && pin.length === 0)}
          startIcon={<Icon name="check_circle" />}
        >
          Check in without payment
        </Button>
        {!inProgress && (
          <Button
            variant="contained"
            onClick={onTakePayment}
            disabled={submitting}
            startIcon={<Icon name="payments" />}
          >
            Take payment
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
